import React, { useState, useEffect } from 'react'
import { Route, Switch, useRouteMatch, useHistory, Redirect } from 'react-router-dom'
import axios from 'axios'

import Stepper from '../ui/stepper'
import LoadingPage from '../leevo_ui/LoadingPage/LoadingPage'
import { Building2, MapPin, BookOpen, CreditCard, Users, X } from 'lucide-react'
import useAuth from '../../utilities/hooks/useAuth'
import toast from '../../utilities/toast'

import AccountCreation from './steps/AccountCreation'
import FacilityInfo from './steps/FacilityInfo'
import CurriculumSelection from './steps/CurriculumSelection'
import StripeConnect from './steps/StripeConnect'
import InviteTeam from './steps/InviteTeam'
import OnboardingComplete from './steps/OnboardingComplete'

function FacilityOnboardingFeature() {
  const { path, url } = useRouteMatch()
  const history = useHistory()
  const { isSignedIn, isLoading: isAuthLoading, verifyCredentials, currentUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [isFacilityLoading, setIsFacilityLoading] = useState(false)
  const [facility, setFacility] = useState(null)
  const [curriculums, setCurriculums] = useState([])
  const [currentStep, setCurrentStep] = useState(1)

  const steps = [
    { id: 'account', label: 'Account', icon: Building2, route: '' },
    { id: 'facility', label: 'Location', icon: MapPin, route: '/facility' },
    { id: 'curriculum', label: 'Curriculum', icon: BookOpen, route: '/curriculum' },
    { id: 'stripe', label: 'Payments', icon: CreditCard, route: '/stripe' },
    { id: 'team', label: 'Team', icon: Users, route: '/team' }
  ]

  useEffect(() => {
    async function loadExistingOnboarding() {
      if (isAuthLoading || !isSignedIn || !currentUser || facility) return

      const userFacility = currentUser.facilitiesUsers?.[0]?.facility
      if (!userFacility?.id) return

      setIsFacilityLoading(true)
      try {
        const response = await axios.get(`/api/facility_onboarding/${userFacility.id}`)
        const facilityData = response.data?.facility?.data
        const onboarding = response.data?.onboarding

        if (onboarding?.complete) {
          history.push(`/facilities/${userFacility.id}/calendar`)
          return
        }

        if (!facilityData) {
          console.error('Invalid response: missing facility data')
          return
        }

        setFacility({
          id: facilityData.id,
          ...facilityData.attributes,
          onboardingStep: onboarding?.step
        })
      } catch (error) {
        console.error('Error loading onboarding:', error)
      } finally {
        setIsFacilityLoading(false)
      }
    }

    loadExistingOnboarding()
  }, [isAuthLoading, isSignedIn, currentUser, facility, history])

  useEffect(() => {
    loadCurriculums()
  }, [])

  useEffect(() => {
    if (facility) {
      const stepMap = {
        account_created: 1,
        facility_info: 2,
        curriculum_selected: 3,
        stripe_connected: 4,
        team_invited: 5,
        completed: 6
      }
      const step = stepMap[facility.onboardingStep] || 1
      setCurrentStep(step)
    }
  }, [facility])

  function loadCurriculums() {
    axios
      .get('/api/facility_onboarding/curriculums')
      .then((response) => {
        const data = response.data?.data || []
        setCurriculums(data.map((c) => ({ id: c.id, ...c.attributes })))
      })
      .catch((error) => {
        console.error('Error loading curriculums:', error)
      })
  }

  function handleStepClick(stepNumber, stepId) {
    const step = steps.find((s) => s.id === stepId)
    if (step && stepNumber <= currentStep) {
      history.push(`${url}${step.route}`)
    }
  }

  function onboardingErrorMessage(error, defaultMessage) {
    if (error.response?.status === 429) {
      return 'Too many attempts. Please wait a few minutes and try again.'
    }
    return defaultMessage
  }

  async function handleAccountCreation(data) {
    setIsLoading(true)
    try {
      const response = await axios.post('/api/facility_onboarding', {
        user: {
          email: data.email,
          password: data.password,
          full_name: data.fullName,
          preferred_name: data.preferredName
        },
        facility: {
          name: data.facilityName,
          time_zone: data.timeZone
        },
        website: data.website,
        team_mascot: data.teamMascot,
        team_founder: data.teamFounder
      })

      const facilityData = response.data?.facility?.data
      if (!facilityData) {
        throw new Error('Invalid response: missing facility data')
      }

      setFacility({
        id: facilityData.id,
        ...facilityData.attributes,
        onboardingStep: response.data.onboarding?.step
      })

      await verifyCredentials()

      setCurrentStep(2)
      history.push(`${url}/facility`)
      toast.success('Account created successfully!')
    } catch (error) {
      console.error('Account creation error:', error)
      const responseData = error.response?.data

      if (error.response?.status === 429) {
        toast.error('Too many attempts. Please wait a few minutes and try again.')
        return
      }
      if (responseData?.existingAccount || responseData?.existingFacility) {
        toast.error(
          <div>
            <p style={{ marginBottom: '8px' }}>{responseData.error}</p>
            <a
              href="/login"
              style={{
                color: '#7c3aed',
                textDecoration: 'underline',
                fontWeight: 600
              }}
            >
              Go to Login →
            </a>
          </div>,
          { duration: 8000 }
        )
      } else {
        const errorMessage =
          responseData?.errors?.email?.[0] || responseData?.error || error.message || 'Failed to create account'
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function handleFacilityInfo(data) {
    setIsLoading(true)
    try {
      const response = await axios.patch(`/api/facility_onboarding/${facility.id}`, {
        facility: {
          address: {
            line1: data.line1,
            line2: data.line2,
            city: data.city,
            state: data.state,
            post_code: data.postCode
          }
        }
      })

      const facilityData = response.data.facility?.data
      setFacility({
        ...facility,
        ...facilityData?.attributes,
        onboardingStep: response.data.onboarding?.step
      })

      setCurrentStep(3)
      history.push(`${url}/curriculum`)
    } catch (error) {
      toast.error(onboardingErrorMessage(error, 'Failed to save facility information'))
    } finally {
      setIsLoading(false)
    }
  }

  async function handleCurriculumSelection(selectedCurriculumIds) {
    setIsLoading(true)
    try {
      const response = await axios.patch(`/api/facility_onboarding/${facility.id}`, {
        facility: {
          curriculum_ids: selectedCurriculumIds
        }
      })

      const facilityData = response.data.facility?.data
      setFacility({
        ...facility,
        ...facilityData?.attributes,
        onboardingStep: response.data.onboarding?.step
      })

      setCurrentStep(4)
      history.push(`${url}/stripe`)
    } catch (error) {
      toast.error(onboardingErrorMessage(error, 'Failed to save curriculum selection'))
    } finally {
      setIsLoading(false)
    }
  }

  async function handleCreateCurriculum(curriculumData) {
    setIsLoading(true)
    try {
      const response = await axios.post(`/api/facility_onboarding/${facility.id}/create_curriculum`, {
        curriculum: {
          name: curriculumData.name,
          sport: curriculumData.sport,
          custom_sport_name: curriculumData.customSportName
        }
      })

      const facilityData = response.data.facility?.data
      setFacility({
        ...facility,
        ...facilityData?.attributes,
        onboardingStep: response.data.onboarding?.step
      })

      setCurrentStep(4)
      history.push(`${url}/stripe`)
      toast.success('Curriculum created successfully!')
    } catch (error) {
      toast.error(onboardingErrorMessage(error, 'Failed to create curriculum'))
    } finally {
      setIsLoading(false)
    }
  }

  function handleStripeConnected() {
    setFacility({
      ...facility,
      isStripeConnected: true,
      onboardingStep: 'stripe_connected'
    })
    setCurrentStep(5)
    history.push(`${url}/team`)
  }

  function handleSkipStripe() {
    setCurrentStep(5)
    history.push(`${url}/team`)
  }

  async function handleTeamInvites(invites) {
    setIsLoading(true)
    try {
      if (invites && invites.length > 0) {
        await axios.post(`/api/facilities/${facility.id}/invitations`, {
          facility_id: facility.id,
          users: invites.map((email) => ({ email })),
          admin: true,
          coach: true
        })
      }

      await axios.post(`/api/facility_onboarding/${facility.id}/complete`)

      setFacility({
        ...facility,
        onboardingStep: 'completed'
      })

      history.push(`${url}/complete`)
    } catch (error) {
      toast.error(onboardingErrorMessage(error, 'Failed to complete onboarding'))
    } finally {
      setIsLoading(false)
    }
  }

  function handleSkipInvites() {
    handleTeamInvites([])
  }

  function handleGoToDashboard() {
    history.push(`/facilities/${facility.id}/calendar`)
  }

  if (isLoading || isAuthLoading || isFacilityLoading) {
    return <LoadingPage />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {facility && currentStep < 6 && (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Stepper steps={steps} currentStep={currentStep} onStepClick={handleStepClick} />
              </div>
              <a href="/" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 ml-4">
                <X className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Exit</span>
              </a>
            </div>
          </div>
        </div>
      )}

      <Switch>
        <Route path={`${path}/complete`}>
          <OnboardingComplete facility={facility} onGoToDashboard={handleGoToDashboard} />
        </Route>

        <Route path={`${path}/team`}>
          {!facility ? (
            <Redirect to={url} />
          ) : (
            <InviteTeam facilityName={facility?.name} onSubmit={handleTeamInvites} onSkip={handleSkipInvites} />
          )}
        </Route>

        <Route path={`${path}/stripe`}>
          {!facility ? (
            <Redirect to={url} />
          ) : (
            <StripeConnect
              facilityId={facility?.id}
              facilityName={facility?.name}
              isConnected={facility?.isStripeConnected}
              onConnected={handleStripeConnected}
              onSkip={handleSkipStripe}
            />
          )}
        </Route>

        <Route path={`${path}/curriculum`}>
          {!facility ? (
            <Redirect to={url} />
          ) : (
            <CurriculumSelection
              curriculums={curriculums}
              selectedIds={facility?.curriculumIds || []}
              facilityName={facility?.name}
              onSubmit={handleCurriculumSelection}
              onCreateCurriculum={handleCreateCurriculum}
            />
          )}
        </Route>

        <Route path={`${path}/facility`}>
          {!facility ? (
            <Redirect to={url} />
          ) : (
            <FacilityInfo facilityName={facility?.name} address={facility?.address} onSubmit={handleFacilityInfo} />
          )}
        </Route>

        <Route path={path}>
          {isSignedIn && facility ? (
            <Redirect to={`${url}/facility`} />
          ) : (
            <AccountCreation onSubmit={handleAccountCreation} />
          )}
        </Route>
      </Switch>
    </div>
  )
}

export default FacilityOnboardingFeature
